import React, {
	forwardRef,
	type ReactElement,
	type ReactNode,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { CloseButton } from '../button';
import Typography from '../Typography.component';

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface Position {
	top: number;
	left: number;
	transform: string;
}

export interface PopoverHandle {
	close: () => void;
}

interface PopoverProps {
	trigger: ReactElement;
	children?: ReactNode;
	title?: string;
	message?: string | ReactNode;
	actionsLeft?: ReactNode;
	actionsRight?: ReactNode;
	placement?: Placement;
	showCloseIcon?: boolean;
	className?: string;
	onShow?: () => void;
	onHide?: () => void;
}

const DEFAULT_POSITION: Position = { top: 0, left: 0, transform: '' };
const GAP = 10;

const calculatePosition = (
	triggerEl: HTMLDivElement,
	placement: Placement
): Position => {
	const rect = triggerEl.getBoundingClientRect();

	const positions: Record<Placement, Position> = {
		top: {
			top: rect.top - GAP,
			left: rect.left + rect.width / 2,
			transform: 'translateX(-50%) translateY(-100%)',
		},
		bottom: {
			top: rect.bottom + GAP,
			left: rect.left + rect.width / 2,
			transform: 'translateX(-50%)',
		},
		left: {
			top: rect.top + rect.height / 2,
			left: rect.left - GAP,
			transform: 'translateX(-100%) translateY(-50%)',
		},
		right: {
			top: rect.top + rect.height / 2,
			left: rect.right + GAP,
			transform: 'translateY(-50%)',
		},
	};

	return positions[placement] || positions.top;
};

const Popover = forwardRef<PopoverHandle, PopoverProps>(
	(
		{
			trigger,
			children,
			title,
			message,
			actionsLeft,
			actionsRight,
			placement = 'top',
			showCloseIcon = true,
			className = '',
			onShow,
			onHide,
		},
		ref
	) => {
		const [isVisible, setIsVisible] = useState(false);
		const [position, setPosition] = useState<Position>(DEFAULT_POSITION);

		const popoverRef = useRef<HTMLDivElement>(null);
		const containerRef = useRef<HTMLDivElement>(null);

		useImperativeHandle(
			ref,
			() => ({
				close: () => {
					setIsVisible(false);
					onHide?.();
				},
			}),
			[onHide]
		);

		const updatePosition = useCallback(() => {
			if (containerRef.current) {
				setPosition(calculatePosition(containerRef.current, placement));
			}
		}, [placement]);

		const handleTriggerClick = useCallback(
			(event: React.MouseEvent): void => {
				event.stopPropagation();
				const willShow = !isVisible;

				if (willShow) {
					updatePosition();
				}

				setIsVisible(willShow);

				if (willShow) {
					onShow?.();
				} else {
					onHide?.();
				}
			},
			[isVisible, updatePosition, onShow, onHide]
		);

		const handleClose = useCallback(
			(event: React.MouseEvent): void => {
				event.stopPropagation();
				setIsVisible(false);
				onHide?.();
			},
			[onHide]
		);

		// Close on outside click
		useEffect(() => {
			if (!isVisible) return;

			const handleClickOutside = (event: Event): void => {
				const target = event.target as Node;
				const isOutsideContainer =
					containerRef.current && !containerRef.current.contains(target);
				const isOutsidePopover =
					popoverRef.current && !popoverRef.current.contains(target);

				if (isOutsideContainer && isOutsidePopover) {
					setIsVisible(false);
					onHide?.();
				}
			};

			document.addEventListener('mousedown', handleClickOutside);
			return () =>
				document.removeEventListener('mousedown', handleClickOutside);
		}, [isVisible, onHide]);

		// Reposition on resize/scroll
		useEffect(() => {
			if (!isVisible) return;

			let debounceTimeout: NodeJS.Timeout;

			const handleReposition = (): void => {
				clearTimeout(debounceTimeout);
				debounceTimeout = setTimeout(updatePosition, 100);
			};

			window.addEventListener('resize', handleReposition);
			window.addEventListener('scroll', handleReposition, true);

			return () => {
				clearTimeout(debounceTimeout);
				window.removeEventListener('resize', handleReposition);
				window.removeEventListener('scroll', handleReposition, true);
			};
		}, [isVisible, updatePosition]);

		const hasActions = actionsLeft || actionsRight;

		const triggerElement = React.cloneElement(trigger, {
			onClick: handleTriggerClick,
			style: {
				...trigger.props.style,
				cursor: 'pointer',
			},
		});

		return (
			<>
				<div ref={containerRef} className={`common-ui-popover ${className}`}>
					{triggerElement}
				</div>

				{isVisible &&
					createPortal(
						<div
							ref={popoverRef}
							className={`popover-content popover-content-${placement} ${showCloseIcon ? 'has-close-icon' : ''} ${className ? ` ${className}` : ''}`}
							data-placement={placement}
							style={{
								position: 'fixed',
								top: `${position.top}px`,
								left: `${position.left}px`,
								transform: position.transform,
								zIndex: 9999,
							}}
							onClick={e => e.stopPropagation()}
							onMouseDown={e => e.stopPropagation()}
						>
							<div className="popover-body">
								<div className="popover-wrapper">
									{title && (
										<Typography.H2 className="popover-title">
											{title}
										</Typography.H2>
									)}
									{message && <div className="popover-message">{message}</div>}

									{showCloseIcon && (
										<CloseButton
											className="close-button popover-close"
											onClick={handleClose}
											ariaLabel="Close popover"
											data-qa="popover-close-button"
										/>
									)}
								</div>

								{hasActions && (
									<div className="popover-actions">
										{actionsLeft && (
											<div className="popover-actions-left">{actionsLeft}</div>
										)}
										{actionsRight && (
											<div className="popover-actions-right">
												{actionsRight}
											</div>
										)}
									</div>
								)}

								{children}
							</div>
						</div>,
						document.body
					)}
			</>
		);
	}
);

export default Popover;
